import React, { useState } from "react";
import { Calculator, Hash, Sparkles, ArrowRight } from "lucide-react";
import PageShell from "../components/PageShell";
import { interpretNumber } from "../toolsApi";
import { useToast } from "../hooks/use-toast";

function Ball({ children }) {
  return (
    <span
      className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 text-white font-extrabold text-lg shadow-md"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {children}
    </span>
  );
}

export default function Interpretar() {
  const [n, setN] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = (n || "").trim();
    if (!v) return;
    if (!/^\d{1,4}$/.test(v)) {
      toast({ title: "Número inválido", description: "Ingresa solo dígitos (0-9999)" });
      return;
    }
    try {
      setLoading(true);
      const r = await interpretNumber(v);
      setData(r);
    } catch (err) {
      toast({ title: "Error", description: "No pudimos interpretar el número." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      eyebrow={<><Calculator className="w-3.5 h-3.5" /> Interpretar Números</>}
      title="Interpretador Numérico"
      subtitle="Ingresa un número y descubre su raíz numerológica, energía, inverso, acompañantes y los sueños asociados según la tradición dominicana."
      accent="amber"
    >
      <div className="bg-white rounded-3xl shadow-xl border border-amber-100 p-6 sm:p-8 mb-8">
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">
              Número a interpretar (0 - 9999)
            </label>
            <div className="flex items-center bg-amber-50/60 border-2 border-amber-100 focus-within:border-amber-500 rounded-xl px-4 py-3">
              <Hash className="w-5 h-5 text-amber-600 mr-2" />
              <input
                data-testid="interpret-input"
                value={n}
                onChange={(e) => setN(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="Ej: 33"
                inputMode="numeric"
                className="flex-1 bg-transparent outline-none text-xl font-extrabold tracking-widest text-amber-900"
              />
            </div>
          </div>
          <button
            data-testid="interpret-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-red-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 transition"
          >
            <ArrowRight className="w-4 h-4" />
            {loading ? "Analizando..." : "Interpretar"}
          </button>
        </form>
      </div>

      {data && (
        <div className="space-y-6">
          {/* Header card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 sm:p-8 border border-amber-200">
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider text-amber-700 mb-2">Tu número</p>
                <span
                  className="inline-flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white font-extrabold text-4xl shadow-2xl"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {data.number}
                </span>
                <p className="mt-3 text-xs text-gray-500">Inverso: <strong className="text-amber-800">{data.inverso}</strong></p>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Suma de dígitos" value={data.sum_digits} />
                  <Stat label="Raíz numerológica" value={data.digital_root} />
                  <Stat label="Inverso" value={data.inverso} />
                </div>
                <div className="bg-white rounded-2xl p-4 border border-amber-200">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Energía</p>
                  <p className="text-sm text-gray-700">{data.energy}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Companions */}
          {data.companions?.length > 0 && (
            <div className="bg-white rounded-3xl shadow-md border border-amber-100 p-6 sm:p-8">
              <h3 className="text-lg font-extrabold text-[#0e4a7a] mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-600" /> Mejores acompañantes
              </h3>
              <div className="flex flex-wrap gap-3">
                {data.companions.map((c) => <Ball key={c}>{c}</Ball>)}
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Estos números surgen del inverso, suma 11, complemento y raíz numerológica de tu número original.
              </p>
            </div>
          )}

          {/* Related dreams */}
          {data.related_dreams?.length > 0 && (
            <div className="bg-white rounded-3xl shadow-md border border-amber-100 p-6 sm:p-8">
              <h3 className="text-lg font-extrabold text-[#0e4a7a] mb-4">Sueños asociados</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.related_dreams.map((d) => (
                  <div key={d.name} className="rounded-2xl border border-amber-100 p-4 bg-amber-50/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-[#0e4a7a]">{d.name}</p>
                      <span
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-red-500 text-white font-extrabold text-sm"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {d.number}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{d.tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-5 border border-amber-200">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">Lectura completa</p>
            <p className="text-sm text-gray-800 leading-relaxed">{data.summary}</p>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl px-3 py-3 border border-amber-100 text-center">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-2xl font-extrabold text-amber-700" style={{ fontFamily: "'Playfair Display', serif" }}>
        {value}
      </p>
    </div>
  );
}
